import { useNavigation } from '@refinedev/core';
import { List, useTable, CreateButton } from '@refinedev/antd';
import { Table, Input, Space, Tag, Avatar, Button, Rate, Badge, Select, Card, Skeleton, Tooltip, message, Row, Col } from 'antd';
import { SearchOutlined, UserOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, FilterOutlined, BellOutlined, SafetyCertificateOutlined, PhoneOutlined, TeamOutlined, CarOutlined, StopOutlined, RiseOutlined, IdcardOutlined, StarFilled, ClockCircleOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { CallNotesModal } from '../../components/CallNotesModal';
import { CallColorFilters, type CallColor } from '../../components/CallColorFilters';
import { VerificationFilter, type VerificationFilterValue } from '../../components/VerificationFilter';
import { LanguageFlag, RolePair } from '../../components/UserBadges';
import { UserFilters, UserFilterValues } from '../../components/filters/UserFilters';
import { usePermission } from '../../hooks/usePermission';
import { t } from '../../i18n';
import { apiClient } from '../../lib/api-client';
import { formatRegisteredAt, resolveCallIconColor } from '../../lib/utils';
import { buildUserListFilters, countDrawerFilters } from '../../lib/userListFilters';

interface Driver {
  id: number;
  fullName: string | null;
  phone: string;
  currentRole: 'CLIENT' | 'DRIVER' | 'ADMIN';
  initialRole: 'CLIENT' | 'DRIVER' | 'ADMIN' | null;
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED' | 'RESTING';
  // Independent of `status` — set when the driver is blocked. The two
  // can fall out of sync (the gateway used to clobber `status` on every
  // location ping), so we render the blocked indicator off this field.
  blockedAt: string | null;
  blockedReason: string | null;
  photoUrl: string | null;
  rating: number;
  totalTrips: number;
  isDriverVerified: boolean;
  fcmToken: string | null;
  createdAt: string;
  pinfl: string | null;
  selfEmploymentStatus: 'REGISTERED' | 'NOT_REGISTERED' | null;
  selfEmploymentReason: string | null;
  selfEmploymentCheckedAt: string | null;
  preferredLang: 'UZ' | 'RU' | 'EN' | null;
  callNotesCount?: number;
  callIconColor?: 'red' | 'green' | 'blue' | 'yellow' | 'gray' | null;
  vehicles?: Array<{
    make: string;
    model: string;
    plate: string;
  }>;
}

export const DriverList = () => {
  const { show } = useNavigation();
  const { can } = usePermission();
  const [filterOpen, setFilterOpen] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [drawerFilters, setDrawerFilters] = useState<UserFilterValues>({});
  const [statusSelect, setStatusSelect] = useState<string | undefined>(undefined);
  const searchTimerRef = useRef<number | null>(null);

  const activeFilterCount = countDrawerFilters(drawerFilters);

  const { tableProps, setFilters, tableQueryResult } = useTable<Driver>({
    resource: 'admin/drivers',
    syncWithLocation: true,
    pagination: {
      pageSize: 20,
    },
    sorters: {
      initial: [{ field: 'createdAt', order: 'desc' }],
    },
  });

  const [callModalDriver, setCallModalDriver] = useState<Driver | null>(null);

  const [callColorFilter, setCallColorFilter] = useState<CallColor | null>(null);
  // Document-verification filter (Tasdiqlangan / Tasdiqlanmagan)
  const [verificationFilter, setVerificationFilter] =
    useState<VerificationFilterValue>(null);

  // Driver stats for the cards above the table
  const [stats, setStats] = useState<{
    total: number;
    verified: number;
    pending: number;
    online: number;
    blocked: number;
    newThisMonth: number;
    averageRating: number | null;
    totalTrips: number;
    withPinfl: number;
    withoutPinfl: number;
    selfEmployedRegistered: number;
    selfEmployedNotRegistered: number;
  } | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/admin/drivers-stats');
        if (!cancelled) setStats(res.data);
      } catch {
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Per-row loading state for the "Check self-employment" action so the
  // spinner only spins on the row being checked.
  const [checkingDriverId, setCheckingDriverId] = useState<number | null>(null);

  const runSelfEmploymentCheck = async (driver: Driver) => {
    if (!driver.pinfl) {
      message.warning(
        "PINFL kiritilmagan — avval haydovchi sahifasida PINFL'ni saqlang",
      );
      return;
    }
    setCheckingDriverId(driver.id);
    try {
      const res = await apiClient.get(
        `/admin/drivers/${driver.id}/self-employment-check`,
      );
      const status = res.data?.status as 'REGISTERED' | 'NOT_REGISTERED';
      const reason = res.data?.reason || '';
      if (status === 'REGISTERED') {
        message.success(`✅ Самозанятый — ${reason || "Ma'lumot topildi"}`);
      } else {
        message.warning(`❌ Не зарегистрирован — ${reason || "Ma'lumot topilmadi"}`);
      }
      // Refresh the list so the cached status badge updates without a
      // full page reload.
      tableProps.onChange?.(
        tableProps.pagination as never,
        {} as never,
        {} as never,
        { action: 'paginate', currentDataSource: [] } as never,
      );
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Soliq tekshiruvi muvaffaqiyatsiz';
      message.error(msg);
    } finally {
      setCheckingDriverId(null);
    }
  };

  // Use `'k' in next` to detect changes — treating the Select's clear-X
  // output (`undefined`) as a deliberate clear instead of "no change".
  const commit = (next: {
    search?: string;
    drawer?: UserFilterValues;
    callColor?: CallColor | null;
    verification?: VerificationFilterValue;
    status?: string | undefined;
  }) => {
    const search = 'search' in next ? next.search! : searchText;
    const drawer = 'drawer' in next ? next.drawer! : drawerFilters;
    const callColor = 'callColor' in next ? next.callColor! : callColorFilter;
    const verification =
      'verification' in next ? next.verification! : verificationFilter;
    const status = 'status' in next ? next.status : statusSelect;
    if ('search' in next) setSearchText(search);
    if ('drawer' in next) setDrawerFilters(drawer);
    if ('callColor' in next) setCallColorFilter(callColor);
    if ('verification' in next) setVerificationFilter(verification);
    if ('status' in next) setStatusSelect(status);
    setFilters(
      buildUserListFilters({ search, drawer, callColor, verification, status }),
      'replace',
    );
  };

  const handleSearchChange = (val: string) => {
    setSearchText(val);
    if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    searchTimerRef.current = window.setTimeout(
      () => commit({ search: val }),
      val === '' ? 0 : 350,
    );
  };

  const handleDrawerApply = (drawer: UserFilterValues) => {
    const { search, status, ...rest } = drawer;
    const update: any = { drawer: rest };
    if (search !== undefined) update.search = search;
    if (status !== undefined) update.status = status;
    commit(update);
  };

  const handleResetFilters = () => {
    if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    commit({
      search: '',
      drawer: {},
      callColor: null,
      verification: null,
      status: undefined,
    });
  };

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) window.clearTimeout(searchTimerRef.current);
    };
  }, []);

  const statusColors: Record<string, string> = {
    ONLINE: 'green',
    OFFLINE: 'default',
    BLOCKED: 'red',
    RESTING: 'orange',
  };

  const statusLabels: Record<string, string> = {
    ONLINE: t('status.online'),
    OFFLINE: t('status.offline'),
    BLOCKED: t('status.blocked'),
    RESTING: t('status.resting'),
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id: number) => <span style={{ fontWeight: 500 }}>#{id}</span>,
    },
    {
      title: t('table.driver'),
      dataIndex: 'fullName',
      key: 'fullName',
      render: (_: string, record: Driver) => (
        <Space>
          <Avatar src={record.photoUrl} icon={<UserOutlined />} />
          <div>
            <div>{record.fullName || t('common.noName')}</div>
            <div style={{ color: '#7F8C8D', fontSize: 12 }}>{record.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: t('table.verified'),
      dataIndex: 'isDriverVerified',
      key: 'isDriverVerified',
      render: (verified: boolean) =>
        verified ? (
          <CheckCircleOutlined style={{ color: '#27AE60', fontSize: 18 }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#E74C3C', fontSize: 18 }} />
        ),
    },
    {
      title: t('drivers.pushNotification'),
      dataIndex: 'fcmToken',
      key: 'fcmToken',
      width: 60,
      align: 'center' as const,
      render: (fcmToken: string | null) => (
        <Tooltip title={fcmToken ? t('filters.hasPush') : t('filters.noPush')}>
          <BellOutlined
            style={{
              color: fcmToken ? '#27AE60' : '#BDC3C7',
              fontSize: 18,
            }}
          />
        </Tooltip>
      ),
    },
    {
      title: t('table.role'),
      key: 'role',
      render: (_: unknown, record: Driver) => (
        <RolePair currentRole={record.currentRole} initialRole={record.initialRole} />
      ),
    },
    {
      title: t('users.language'),
      key: 'preferredLang',
      width: 60,
      render: (_: unknown, record: Driver) => <LanguageFlag lang={record.preferredLang} />,
    },
    {
      title: t('table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: Driver) => (
        <Space direction="vertical" size={2}>
          <Tag color={statusColors[status]}>{statusLabels[status] || status}</Tag>
          {record.blockedAt && (
            // Show an explicit blocked indicator independently of `status`.
            // A bug in the realtime gateway used to overwrite the BLOCKED
            // status to ONLINE on every location ping while leaving
            // `blockedAt` set — this badge surfaces that inconsistency
            // and makes blocked drivers visible regardless of status.
            <Tooltip title={record.blockedReason || t('status.blocked')}>
              <Tag color="red" style={{ fontSize: 11, lineHeight: '16px' }}>
                🔒 {t('status.blocked')}
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: t('table.rating'),
      dataIndex: 'rating',
      key: 'rating',
      render: (rating: number | string) => {
        const num = Number(rating) || 0;
        return (
          <Space>
            <Rate disabled defaultValue={num} count={5} style={{ fontSize: 12 }} />
            <span>{num.toFixed(1)}</span>
          </Space>
        );
      },
    },
    {
      title: t('table.trips'),
      dataIndex: 'totalTrips',
      key: 'totalTrips',
    },
    {
      title: t('table.registered'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      defaultSortOrder: 'descend' as const,
      render: (date: string) => formatRegisteredAt(date),
    },
    {
      title: 'Самозанятость',
      key: 'selfEmployment',
      render: (_: unknown, record: Driver) => {
        if (!record.pinfl) {
          return (
            <Tooltip title="PINFL kiritilmagan">
              <Tag color="default">—</Tag>
            </Tooltip>
          );
        }
        if (record.selfEmploymentStatus === 'REGISTERED') {
          return (
            <Tooltip
              title={
                record.selfEmploymentCheckedAt
                  ? `Tekshirildi: ${new Date(record.selfEmploymentCheckedAt).toLocaleString('uz-UZ')}`
                  : undefined
              }
            >
              <Tag color="green" icon={<CheckCircleOutlined />}>Самозанятый</Tag>
            </Tooltip>
          );
        }
        if (record.selfEmploymentStatus === 'NOT_REGISTERED') {
          return (
            <Tooltip
              title={
                record.selfEmploymentCheckedAt
                  ? `Tekshirildi: ${new Date(record.selfEmploymentCheckedAt).toLocaleString('uz-UZ')}`
                  : undefined
              }
            >
              <Tag color="red" icon={<CloseCircleOutlined />}>Не зарегистрирован</Tag>
            </Tooltip>
          );
        }
        return <Tag color="default">Не проверен</Tag>;
      },
    },
    {
      title: '',
      key: 'call',
      width: 60,
      render: (_: unknown, record: Driver) => {
        const color = resolveCallIconColor(record.callIconColor, record.callNotesCount);
        const called = (record.callNotesCount ?? 0) > 0;
        return (
          <Tooltip
            title={
              record.callIconColor
                ? `Цветная метка: ${record.callIconColor}`
                : called
                  ? `Был(а) звонок: ${record.callNotesCount}`
                  : 'Звонков ещё не было — добавить заметку'
            }
          >
            <Button
              type="text"
              icon={<PhoneOutlined style={{ color }} />}
              onClick={(e) => {
                e.stopPropagation();
                setCallModalDriver(record);
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: t('table.actions'),
      key: 'actions',
      render: (_: unknown, record: Driver) => (
        <Space size={4}>
          <Tooltip title="Самозанятость bo'yicha tekshirish">
            <Button
              type="text"
              icon={<SafetyCertificateOutlined />}
              loading={checkingDriverId === record.id}
              disabled={!record.pinfl}
              onClick={(e) => {
                e.stopPropagation();
                runSelfEmploymentCheck(record);
              }}
            />
          </Tooltip>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              show('admin/drivers', record.id);
            }}
          />
        </Space>
      ),
    },
  ];

  // Stat card descriptors — colour-coded so the operator can scan quickly.
  const statCards = stats
    ? [
        {
          key: 'total',
          title: 'Всего водителей',
          value: stats.total,
          icon: <TeamOutlined />,
          color: '#1F2937',
          bg: '#F3F4F6',
        },
        {
          key: 'verified',
          title: 'Верифицированных',
          value: stats.verified,
          suffix: stats.total
            ? `/ ${stats.total}`
            : undefined,
          icon: <CheckCircleOutlined />,
          color: '#10B981',
          bg: '#ECFDF5',
        },
        {
          key: 'pending',
          title: 'На проверке',
          value: stats.pending,
          icon: <ClockCircleOutlined />,
          color: '#F59E0B',
          bg: '#FFFBEB',
        },
        {
          key: 'online',
          title: 'Онлайн сейчас',
          value: stats.online,
          icon: <CarOutlined />,
          color: '#3B82F6',
          bg: '#EFF6FF',
        },
        {
          key: 'blocked',
          title: 'Заблокированных',
          value: stats.blocked,
          icon: <StopOutlined />,
          color: '#EF4444',
          bg: '#FEF2F2',
        },
        {
          key: 'newThisMonth',
          title: 'Новых в этом месяце',
          value: stats.newThisMonth,
          icon: <RiseOutlined />,
          color: '#8B5CF6',
          bg: '#F5F3FF',
        },
        {
          key: 'rating',
          title: 'Средний рейтинг',
          value: stats.averageRating != null ? Number(stats.averageRating).toFixed(2) : '—',
          icon: <StarFilled />,
          color: '#F59E0B',
          bg: '#FFFBEB',
        },
        {
          key: 'totalTrips',
          title: 'Поездок выполнено',
          value: stats.totalTrips,
          icon: <RiseOutlined />,
          color: '#0EA5E9',
          bg: '#F0F9FF',
        },
        {
          key: 'pinfl',
          title: 'С PINFL',
          value: stats.withPinfl,
          suffix: stats.total ? `/ ${stats.total}` : undefined,
          icon: <IdcardOutlined />,
          color: '#0F766E',
          bg: '#F0FDFA',
        },
        {
          key: 'selfEmployedRegistered',
          title: 'Самозанятых',
          value: stats.selfEmployedRegistered,
          icon: <SafetyCertificateOutlined />,
          color: '#10B981',
          bg: '#ECFDF5',
        },
        {
          key: 'selfEmployedNotRegistered',
          title: 'Не самозанятых',
          value: stats.selfEmployedNotRegistered,
          icon: <SafetyCertificateOutlined />,
          color: '#EF4444',
          bg: '#FEF2F2',
        },
      ]
    : [];

  return (
    <>
      {/* Stat cards above the table */}
      {statsLoading ? (
        <Card style={{ marginBottom: 16 }}>
          <Skeleton active paragraph={{ rows: 2 }} />
        </Card>
      ) : stats ? (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          {statCards.map((c) => (
            <Col key={c.key} xs={12} sm={8} md={6} lg={4}>
              <Card
                size="small"
                bordered={false}
                style={{
                  background: c.bg,
                  borderRadius: 10,
                  height: '100%',
                }}
                bodyStyle={{ padding: 12 }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: c.color,
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {c.icon}
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: '#6B7280',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {c.title}
                    </div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: c.color,
                        lineHeight: 1.2,
                      }}
                    >
                      {c.value}
                      {c.suffix && (
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: '#9CA3AF',
                            marginLeft: 4,
                          }}
                        >
                          {c.suffix}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      ) : null}

      <List
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 6 }}>
            <span>{t('drivers.title')}</span>
            <CallColorFilters
              value={callColorFilter}
              onChange={(color) => commit({ callColor: color })}
            />
            <VerificationFilter
              value={verificationFilter}
              onChange={(v) => commit({ verification: v })}
            />
          </span>
        }
        headerButtons={
          <Space>
            <Input
              placeholder={t('common.search')}
              prefix={<SearchOutlined />}
              allowClear
              value={searchText}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              placeholder={t('table.status')}
              allowClear
              style={{ width: 130 }}
              value={statusSelect}
              onChange={(value) => commit({ status: value })}
              options={[
                { value: 'ONLINE', label: t('status.online') },
                { value: 'OFFLINE', label: t('status.offline') },
                { value: 'RESTING', label: t('status.resting') },
                { value: 'BLOCKED', label: t('status.blocked') },
              ]}
            />
            <Badge count={activeFilterCount} size="small">
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFilterOpen(true)}
              >
                {t('common.filter')}
              </Button>
            </Badge>
            {can('drivers', 'create') && <CreateButton>{t('drivers.newDriver')}</CreateButton>}
          </Space>
        }
      >
        <Table
          {...tableProps}
          columns={columns}
          rowKey="id"
          onRow={(record) => ({
            onClick: () => show('admin/drivers', record.id),
            style: { cursor: 'pointer' },
          })}
          loading={
            tableProps.loading
              ? { indicator: <Card bordered={false}><Skeleton active paragraph={{ rows: 8 }} /></Card> }
              : false
          }
        />
      </List>

      <UserFilters
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleDrawerApply}
        onReset={handleResetFilters}
        filterType="drivers"
        initialValues={drawerFilters}
      />

      <CallNotesModal
        open={!!callModalDriver}
        userId={callModalDriver?.id ?? null}
        userName={callModalDriver?.fullName ?? undefined}
        userPhone={callModalDriver?.phone}
        currentColor={callModalDriver?.callIconColor ?? null}
        onClose={() => setCallModalDriver(null)}
        onChanged={() => tableQueryResult?.refetch?.()}
      />
    </>
  );
};
